import { Input as AntInput } from "antd";
import classNames from "classnames";
import { ReadonlyArray, pipe } from "fp";

import React from "react";

import * as Data from "~/Data";

export function Input(props: Input.Props) {
  return <AntInput {...props} />;
}

export namespace Input {
  export type Props = React.ComponentProps<typeof AntInput>;
}

export namespace Select {
  export type Select = {
    readonly values: readonly string[];
    readonly selected: string;
    readonly placeholder?: string;
  };

  export const Component: React.Fn<Select & React.HTML<"select">> = ({
    className,
    values,
    selected,
    placeholder,
    ...props
  }) => (
    <select
      {...props}
      placeholder={placeholder ?? "none"}
      className={classNames("select", className)}
      value={selected.trim()}
    >
      {values.map((value, index) => (
        <option value={value.trim()} key={index}>
          {value}
        </option>
      ))}
    </select>
  );
}

export namespace Search {
  export type Search<A> = Input.Props & {
    readonly name: string;
    readonly invalid?: boolean;
    readonly data: Data<A>;
    readonly selected?: Data.Reference.Reference;
    readonly onSelected: (selected?: Data.Reference.Reference) => void;
  };

  export type Data<A> = readonly Datum<A>[];

  export type Datum<A> = Data.Reference.Reference & {
    readonly text?: string;
  } & A;

  export const Component = function <A>({
    name,
    data,
    selected,
    onSelected,
    ...props
  }: Search<A>): JSX.Element {
    return (
      <>
        <Input
          {...props}
          list={name}
          onChange={({ target: { value } }) =>
            value === ""
              ? onSelected()
              : pipe(
                  data.find(({ id, text }) => text === value || id === value),
                  (datum) =>
                    datum ? onSelected(datum) : onSelected({ id: value })
                )
          }
        />

        <datalist id={name}>
          {pipe(
            data,
            ReadonlyArray.takeLeft(20),
            ReadonlyArray.map((datum) => (
              <option
                key={datum.id}
                id={datum.id}
                value={datum.text ?? datum.id}
              />
            ))
          )}
        </datalist>
      </>
    );
  };
}
